import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const filterChips = ["TUTTI", "NORMATIVE", "TECNOLOGIA", "SICUREZZA", "MANUTENZIONE"];
const coverPalettes = [{
  bg: "bg-white",
  text: "text-primary",
  rule: "bg-primary",
  accent: "text-accent"
}, {
  bg: "bg-primary",
  text: "text-white",
  rule: "bg-accent",
  accent: "text-accent"
}, {
  bg: "bg-secondary",
  text: "text-white",
  rule: "bg-accent",
  accent: "text-accent"
}];
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("it-IT", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function estimateReadingTime(content?: string, excerpt?: string): string {
  const text = stripHtml(content) || stripHtml(excerpt) || "";
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(3, Math.ceil(words / 200));
  return `${minutes} MIN LETTURA`;
}
function RecentChaptersGrid() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="1156" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] bg-slate-200 border border-slate-300 animate-pulse rounded-2xl" />)}
      </div>;
  }
  if (error) {
    return <div className="border border-slate-300 bg-slate-100 p-10 text-center rounded-2xl">
        <p className="font-default italic text-slate-700 mb-4">
          L'archivio tecnico non è attualmente disponibile.
        </p>
        <Button onClick={refetch} variant="outline" className="font-default uppercase tracking-[0.16em] text-xs rounded-lg border-primary text-primary hover:bg-primary hover:text-white">
          Riprova
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border border-slate-300 bg-slate-100 p-16 text-center rounded-2xl">
        <p className="font-default uppercase tracking-[0.18em] text-xs text-primary mb-3">
          § A.000 / AS — ARCHIVIO VUOTO
        </p>
        <p className="font-default italic text-lg text-slate-700">
          Nessun approfondimento tecnico disponibile al momento.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
      {posts.map((post, i) => {
      const palette = coverPalettes[i % coverPalettes.length];
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "TECNICA";
      const indexLabel = `A.${String(i + 1).padStart(3, "0")}${String.fromCharCode(97 + i % 3)}`;
      const editionNo = `No. ${String(i + 1).padStart(2, "0")}`;
      const cleanTitle = stripHtml(post.title);
      return <article data-index={i} key={post.id} className="group bg-white border border-[var(--border)] rounded-2xl overflow-hidden transition-all duration-500 ease-out hover:-translate-y-2 shadow-lg hover:shadow-2xl">
            <Link to={post.link || "#"} className="block">
              <div className={`relative aspect-[3/4] ${palette.bg} ${palette.text} overflow-hidden transition-transform duration-500 ease-out group-hover:scale-[1.02] border-b border-[var(--border)]/10`}>
                <div className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay" style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, currentColor 0%, transparent 2%), radial-gradient(circle at 70% 60%, currentColor 0%, transparent 1.5%), radial-gradient(circle at 40% 80%, currentColor 0%, transparent 2%)"
            }} />

                <div className="absolute top-8 left-8 right-8 flex items-start justify-between">
                  <span className={`font-mono text-[0.7rem] uppercase tracking-[0.2em] font-medium ${palette.accent}`}>
                    § {indexLabel}
                  </span>
                  <span className={`font-default text-[0.65rem] uppercase tracking-[0.16em] opacity-60`}>
                    {category}
                  </span>
                </div>

                <div className="absolute inset-x-8 top-1/2 -translate-y-1/2">
                  <h3 className="font-default font-bold leading-[1.1] tracking-tight" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2rem)"
              }}>
                    {cleanTitle}
                  </h3>
                  <div className="mt-8 flex items-center">
                    <span className={`h-px w-12 ${palette.rule} transition-all duration-500 ease-out group-hover:w-24`} />
                    <span className={`ml-2 h-1.5 w-1.5 rounded-lg ${palette.rule}`} />
                  </div>
                </div>

                <div className="absolute bottom-8 left-8 right-8 flex items-end justify-between">
                  <span className={`font-default text-[0.65rem] uppercase tracking-[0.2em] opacity-50`}>
                    Aera Tech
                  </span>
                  <span className={`font-mono text-[0.65rem] uppercase tracking-[0.16em] ${palette.accent}`}>
                    {editionNo}
                  </span>
                </div>
              </div>

              <div className={`px-8 py-6 flex items-center justify-between flex-wrap gap-4 ${palette.bg === 'bg-white' ? 'bg-white' : 'bg-secondary/5'}`}>
                <div className="flex items-center gap-4 font-default text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground">
                  <span>{formatDate(post.date)}</span>
                  <span className="h-1 w-1 rounded-lg bg-accent" />
                  <span className="text-accent font-medium">{category}</span>
                </div>
                <span className="font-default text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground">
                  {estimateReadingTime(post.content, post.excerpt)}
                </span>
              </div>
            </Link>
          </article>;
    })}
    </div>;
}
export default function TechnicalPostArchive() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="technicalpostarchive" className="relative bg-[#F4F6F8] text-slate-900 py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 12% 18%, hsl(222 47% 7%) 0%, transparent 1.5%), radial-gradient(circle at 78% 32%, hsl(222 47% 7%) 0%, transparent 1.2%), radial-gradient(circle at 35% 70%, hsl(222 47% 7%) 0%, transparent 1.8%), radial-gradient(circle at 88% 85%, hsl(222 47% 7%) 0%, transparent 1.4%), radial-gradient(circle at 50% 50%, hsl(222 47% 7%) 0%, transparent 1%)"
    }} />

      <div className="relative max-w-[1320px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <p className="font-default text-[0.75rem] uppercase tracking-[0.18em] text-secondary mb-6">
              § A.001 / AS — APPROFONDIMENTI TECNICI
            </p>
            <h2 className="font-default font-bold text-secondary leading-[1.02] tracking-[-0.02em]" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 4.75rem)"
          }}>
              Approfondimenti tecnici per impianti aria e sistemi di aspirazione
            </h2>
            <div className="mt-8 flex items-center">
              <span className="h-px w-16 bg-secondary" />
              <span className="ml-2 h-1.5 w-1.5 rounded-lg bg-primary" />
            </div>
          </div>

          <div className="lg:col-span-5 lg:pt-4">
            <p className="font-default italic text-slate-700 leading-[1.55]" style={{
            fontSize: "clamp(0.95rem, 1.1vw, 1.0625rem)"
          }}>
              Analisi, metodo e indicazioni operative per la gestione tecnica di condotti HVAC, cappe professionali, UTA, ventilatori e reti di estrazione.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {filterChips.map((chip, i) => <Badge data-index={i} key={chip} variant="outline" className="font-default text-[0.65rem] uppercase tracking-[0.16em] rounded-lg border-primary text-primary bg-transparent px-2.5 py-1 cursor-pointer hover:bg-primary hover:text-white transition-colors">
                  {chip}
                </Badge>)}
            </div>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <RecentChaptersGrid />
        </PostsProvider>

        <div className="mt-24 md:mt-32 bg-[#001F3F] rounded-2xl overflow-hidden relative border-t-4 border-accent">
          <div className="px-6 py-16 md:py-20 text-center max-w-3xl mx-auto">
            <h3 className="font-default font-bold text-white text-3xl md:text-4xl mb-6">
              Vuoi valutare lo stato tecnico del tuo impianto?
            </h3>
            <p className="font-default text-white/80 text-lg mb-10 leading-relaxed">
              Un'ispezione tecnica approfondita permette di identificare criticità invisibili, ottimizzare i consumi energetici e garantire la massima sicurezza operativa dei sistemi di estrazione e trattamento aria.
            </p>
            <Link to="/TechnicalInspectionPage">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-white font-default uppercase tracking-widest px-10 py-7 rounded-lg">
                Richiedi sopralluogo tecnico
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}